import { type EventInteractionWithContext, EventInteractions } from '../types';
import { BaseInteractionsLogger } from './BaseInteractionsLogger';
import { DatadogAdsEvents } from './DatadogRUMEvents';

// Type declaration for Datadog RUM
declare global {
  interface Window {
    DD_RUM?: {
      addAction: (name: string, data: unknown) => void;
      onReady: (callback: () => void) => void;
      addError?: (error: unknown, context?: object) => void;
    };
  }
}

/**
 * Adds a custom RUM event to Datadog
 *
 * @param name - Event name
 * @param data - Event payload
 * @returns True if event was queued, false otherwise
 */
export function addCustomRUMEvent<EventName = string, Payload = Record<string, unknown>>(
  name: EventName,
  data: Payload,
): boolean {
  if (typeof window === 'undefined') return false;

  if (!window?.DD_RUM?.addAction || !window?.DD_RUM?.onReady) {
    console.warn('Datadog RUM not initialized. Call initializeDatadog() first.');
    return false;
  }

  try {
    window.DD_RUM.onReady(() => {
      window.DD_RUM?.addAction(String(name), data);
    });
    return true;
  } catch (error) {
    console.error('Failed to send Datadog event:', error);
    return false;
  }
}

type InteractionDatadogLoggerData = {
  action: EventInteractions;
  place: string;
  placementId: string;
};

export class InteractionsDatadogLogger extends BaseInteractionsLogger<InteractionDatadogLoggerData> {
  protected allowedEvents: EventInteractions[] = [EventInteractions.Impression, EventInteractions.Mounted];

  private readonly interactions_history: Set<string> = new Set();

  /**
   * Tracks event card interactions for immediate Datadog logging
   * @param interaction - The interaction to track
   */
  public trackEventCardInteraction({
    id,
    place,
    action,
  }: Pick<EventInteractionWithContext, 'id' | 'place' | 'action'>) {
    const interactionUniqueId = `${id}-${place?.placementId}-${action}`;
    if (!this.allowedEvents.includes(action) || this.interactions_history.has(interactionUniqueId)) return;

    this.interactions_history.add(interactionUniqueId);
    const datadogEvent = this.getDatadogEvent(action);
    if (datadogEvent) {
      this.sendInteractionEvent(place.name, datadogEvent);
    }
  }

  /**
   * Transforms an interaction for Datadog logging
   *
   * @param interaction - The interaction to transform
   * @returns Datadog-specific data format
   */
  protected transformInteraction(interaction: EventInteractionWithContext): InteractionDatadogLoggerData {
    return {
      action: interaction.action,
      place: interaction.place.name,
      placementId: interaction.place.placementId ?? '',
    };
  }

  /**
   * Logs interactions to Datadog RUM
   * - Only logs impressions with placement_id
   * - Only sends RequestedImpression events
   *
   * @param transformedInteractions - Array of transformed interactions
   */
  protected log(transformedInteractions: InteractionDatadogLoggerData[]): void {
    this.sendRequestedImpressionMetrics(transformedInteractions);
  }

  /**
   * Maps interaction actions to Datadog event names
   *
   * @param action - The interaction action
   * @returns Datadog event name or undefined
   */
  private getDatadogEvent(action: EventInteractions): DatadogAdsEvents | undefined {
    const datadogEventsMap: Partial<Record<EventInteractions, DatadogAdsEvents>> = {
      [EventInteractions.Impression]: DatadogAdsEvents.Impression,
      [EventInteractions.Mounted]: DatadogAdsEvents.Mounted,
    };
    return datadogEventsMap[action];
  }

  /**
   * Sends an interaction event to Datadog RUM
   *
   * @param interfaceName - The interface name (e.g., 'related_events')
   * @param action - The Datadog event action
   * @param count - Number of events (default: 1)
   */
  private sendInteractionEvent(interfaceName: string, action: DatadogAdsEvents, count = 1): void {
    addCustomRUMEvent(action, {
      value: count,
      app: 'web',
      is_mobile: false, // TODO: use CF header to determine if mobile
      interface: interfaceName,
    });
  }

  /**
   * Sends Datadog metrics for impressions with placement_id
   *
   * - Filters for impressions with placement_id
   * - Sends RequestedImpression events only
   *
   * @param transformedInteractions - Array of transformed interactions
   */
  private sendRequestedImpressionMetrics(transformedInteractions: InteractionDatadogLoggerData[]): void {
    const impressions = transformedInteractions.filter(
      (interaction) => interaction.action === EventInteractions.Impression && !!interaction.placementId,
    );

    impressions.forEach((interaction) => {
      this.sendInteractionEvent(interaction.place, DatadogAdsEvents.RequestedImpression);
    });
  }
}
